import React, { useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { Menu, X, ArrowUpRight } from "lucide-react";
import FloatingContact from "@/components/FloatingContact";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_omniserve-redesign/artifacts/82hl9a45_OmniServe%20Solutions%20Logo%20Design.png";

function LanguageSwitcher({ onSelect }) {
  const { lang, setLang, languages } = useLang();
  return (
    <div className="flex items-center gap-1 bg-[#F5F5F4] rounded-full p-1" data-testid="language-switcher">
      {languages.map((l) => (
        <button
          key={l.code}
          onClick={() => { setLang(l.code); onSelect && onSelect(); }}
          data-testid={`lang-switcher-${l.code}`}
          className={`text-[11px] font-medium px-2.5 py-1 rounded-full transition-all ${
            lang === l.code
              ? "bg-white text-[#0A0A0A] shadow-sm"
              : "text-[#71717A] hover:text-[#0A0A0A]"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
}

export function Navbar() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);

  const linkClass = ({ isActive }) =>
    `text-sm transition-colors ${isActive ? "text-[#0A0A0A] font-medium" : "text-[#52525B] hover:text-[#0A0A0A]"}`;

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-[#E9E9E7]" data-testid="site-navbar">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-12 py-4 flex items-center justify-between gap-6">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
          <div className="w-9 h-9 flex items-center justify-center">
            <img src={LOGO_URL} alt="OmniServe" className="w-full h-full object-contain" />
          </div>
          <span className="font-display text-xl font-semibold tracking-tight text-[#0A0A0A]">OmniServe - Solutions</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          <NavLink to="/" end className={linkClass} data-testid="nav-home">{t.nav.home}</NavLink>
          <NavLink to="/services" className={linkClass} data-testid="nav-services">{t.nav.services}</NavLink>
          <NavLink
            to="/steuer"
            data-testid="nav-tax"
            className={({ isActive }) =>
              `inline-flex items-center gap-1.5 text-sm transition-colors ${
                isActive ? "text-[#EA6A1A] font-medium" : "text-[#EA6A1A] hover:text-[#C9560F] font-medium"
              }`
            }
          >
            <span className="w-1.5 h-1.5 bg-[#EA6A1A] rounded-full" />
            {t.nav.tax}
          </NavLink>
          <NavLink to="/about" className={linkClass} data-testid="nav-about">{t.nav.about}</NavLink>
          <NavLink to="/contact" className={linkClass} data-testid="nav-contact">{t.nav.contact}</NavLink>
        </nav>

        <div className="hidden lg:flex items-center gap-5">
          <LanguageSwitcher />
          <Link
            to="/contact"
            data-testid="nav-cta"
            className="group inline-flex items-center gap-2 bg-[#0A0A0A] text-white hover:bg-[#EA6A1A] text-sm px-4 py-2.5 rounded-full transition-all shadow-sm"
          >
            {t.nav.cta}
            <ArrowUpRight size={14} className="group-hover:rotate-45 transition-transform" />
          </Link>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          className="lg:hidden text-[#0A0A0A]"
          aria-label="menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#E9E9E7] bg-white" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-5">
            <NavLink to="/" end className={linkClass} onClick={() => setOpen(false)}>{t.nav.home}</NavLink>
            <NavLink to="/services" className={linkClass} onClick={() => setOpen(false)}>{t.nav.services}</NavLink>
            <NavLink to="/steuer" className="text-sm text-[#EA6A1A] font-medium inline-flex items-center gap-1.5" onClick={() => setOpen(false)}>
              <span className="w-1.5 h-1.5 bg-[#EA6A1A] rounded-full" />
              {t.nav.tax}
            </NavLink>
            <NavLink to="/about" className={linkClass} onClick={() => setOpen(false)}>{t.nav.about}</NavLink>
            <NavLink to="/contact" className={linkClass} onClick={() => setOpen(false)}>{t.nav.contact}</NavLink>
            <div className="pt-2"><LanguageSwitcher onSelect={() => setOpen(false)} /></div>
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className="bg-[#0A0A0A] text-white text-sm px-5 py-3 text-center rounded-full"
            >
              {t.nav.cta}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const { t } = useLang();
  const year = new Date().getFullYear();
  return (
    <footer className="bg-[#0A0A0A] text-white" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-12 pt-20 pb-10">
        <div className="grid md:grid-cols-12 gap-10 pb-14 border-b border-white/10">
          <div className="md:col-span-5">
            <Link to="/" className="flex items-center gap-2.5 mb-8">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center">
                <img src={LOGO_URL} alt="OmniServe" className="w-8 h-8 object-contain" />
              </div>
              <span className="font-display text-2xl font-semibold tracking-tight">OmniServe - Solutions</span>
            </Link>
            <p className="font-display text-3xl leading-[1.15] max-w-md text-white/90 tracking-tight">
              {t.footer.tagline}
            </p>
          </div>

          <div className="md:col-span-3 md:col-start-7">
            <div className="text-xs text-[#EA6A1A] uppercase tracking-wider mb-5 font-medium">Navigation</div>
            <ul className="space-y-3">
              <li><Link to="/" className="text-white/70 hover:text-white text-sm transition-colors">{t.nav.home}</Link></li>
              <li><Link to="/services" className="text-white/70 hover:text-white text-sm transition-colors">{t.nav.services}</Link></li>
              <li><Link to="/about" className="text-white/70 hover:text-white text-sm transition-colors">{t.nav.about}</Link></li>
              <li><Link to="/contact" className="text-white/70 hover:text-white text-sm transition-colors">{t.nav.contact}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="text-xs text-[#EA6A1A] uppercase tracking-wider mb-5 font-medium">Kontakt</div>
            <ul className="space-y-3 text-sm text-white/70">
              <li>Rigistrasse 2, 5610 Wohlen</li>
              <li><a href="tel:+41783111584" className="hover:text-white transition-colors">+41 78 311 15 84</a></li>
              <li><a href="mailto:info@omniserve-solutions.ch" className="hover:text-white transition-colors">info@omniserve-solutions.ch</a></li>
            </ul>
          </div>
        </div>

        <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-xs text-white/50">© {year} OmniServe-Solutions · {t.footer.rights}</p>
          <div className="flex items-center gap-6">
            <a href="#" className="text-xs text-white/50 hover:text-white transition-colors">{t.footer.privacy}</a>
            <a href="#" className="text-xs text-white/50 hover:text-white transition-colors">{t.footer.imprint}</a>
            <span className="inline-flex items-center gap-1.5 text-xs text-white/50">
              <span className="w-1.5 h-1.5 bg-[#EA6A1A] rounded-full" />
              Made in Switzerland
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  const location = useLocation();
  const { lang } = useLang();
  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);
  return (
    <div className="min-h-screen bg-white text-[#0A0A0A] font-sans flex flex-col">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
      <FloatingContact lang={lang} />
    </div>
  );
}
